/**
 * Unified API Client - Phase 8
 *
 * Single source of truth for all API calls.
 * Uses Firebase Auth for token management.
 * NO mock data fallbacks.
 *
 * Field naming follows CLAUDE.md standards:
 * - userId (not workerId)
 * - organizationId (not organization)
 * - workerDetails.trade (not trade)
 */

import { getFirebase } from '../../firebase/lazy';
import { API_BASE_URL } from '../../config/api.config';
import { getAuthToken as getStoredToken, decodeJwtPayload, getAuthTokenAsync } from '../../utils/authToken';

// ============================================================================
// Configuration
// ============================================================================

const createRequestId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `vh-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Environment detection — use MODE only (PROD is true for all vite build outputs including staging)
const isProduction = (): boolean => {
  return import.meta.env.MODE === 'production';
};

// ============================================================================
// Error Types
// ============================================================================

export class ApiError extends Error {
  status: number;
  endpoint: string;
  data?: unknown;

  constructor(message: string, status: number, endpoint: string, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.endpoint = endpoint;
    this.data = data;
  }
}

/** True when API returned 400 MISSING_ORGANIZATION_CONTEXT (dashboard routes). */
export function isMissingOrganizationContextError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 400) return false;
  const d = error.data as { error?: string | { code?: string } } | undefined;
  if (!d) return false;
  if (d.error === 'MISSING_ORGANIZATION_CONTEXT') return true;
  const nested = d.error;
  if (nested && typeof nested === 'object' && nested !== null && 'code' in nested) {
    return (nested as { code?: string }).code === 'MISSING_ORGANIZATION_CONTEXT';
  }
  return false;
}

/**
 * Build a user-visible message from API JSON error bodies.
 * Supports ApiResponse shape ({ error: { message, details } }) and legacy flat { error: string }.
 */
export function normalizeApiErrorMessage(errorData: unknown, httpStatus: number): string {
  if (errorData == null || typeof errorData !== 'object') {
    return `API Error: ${httpStatus}`;
  }
  const d = errorData as Record<string, unknown>;

  if (typeof d.message === 'string' && d.message.trim()) {
    return d.message;
  }

  const err = d.error;
  const detailsStr = typeof d.details === 'string' ? d.details.trim() : '';
  if (typeof err === 'string' && err.trim()) {
    const errStr = err.trim();
    // Many routes put a generic top-level `error` ("Failed to create X") and the actionable text in `details`.
    if (detailsStr && errStr.startsWith('Failed to ') && detailsStr !== errStr) {
      return detailsStr;
    }
    return errStr;
  }

  if (err && typeof err === 'object') {
    const box = err as Record<string, unknown>;
    if (typeof box.message === 'string' && box.message.trim()) {
      return box.message;
    }
    const details = box.details;
    if (Array.isArray(details) && details.length > 0) {
      const first = details[0] as { msg?: string; message?: string };
      const msg = first?.msg ?? first?.message;
      if (typeof msg === 'string' && msg.trim()) {
        return msg;
      }
    }
  }

  if (detailsStr) {
    return detailsStr;
  }

  return `API Error: ${httpStatus}`;
}

// ============================================================================
// Token Management
// ============================================================================

/**
 * Get auth token for authenticated requests.
 * Checks localStorage first (supports JWT adapter), then falls back to Firebase.
 */
async function getAuthToken(): Promise<string | null> {
  // Check localStorage for stored token (JWT adapter or cached Firebase token)
  const stored = getStoredToken();
  if (stored) {
    const payload = decodeJwtPayload(stored);
    const isFirebase = payload?.iss?.startsWith('https://securetoken.google.com/');

    // For non-Firebase tokens (JWT adapter), return if not expired
    if (!isFirebase) {
      const expMs = payload?.exp ? payload.exp * 1000 : null;
      const isExpired = expMs ? Date.now() >= expMs : false;
      if (!isExpired) return stored;
    }

    // For Firebase tokens, return if fresh (2-min buffer)
    if (isFirebase) {
      const expMs = payload?.exp ? payload.exp * 1000 : null;
      const isFresh = expMs ? Date.now() < expMs - 120000 : false;
      if (isFresh) return stored;
    }
  }

  // Fall back to Firebase for fresh token
  try {
    const firebase = await getFirebase();
    const user = firebase.auth.currentUser;
    if (!user) {
      return stored || null;
    }
    return await user.getIdToken();
  } catch (error) {
    // In JWT mode, Firebase may not be available — return stored token
    return stored || null;
  }
}

// ============================================================================
// Core Client
// ============================================================================

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  skipAuth?: boolean;
  body?: unknown;
  /** Internal: true after a single 401 refresh-and-retry. Do not set from callers. */
  _authRetry?: boolean;
}

/**
 * Core API client function
 *
 * @param method - HTTP method
 * @param endpoint - API endpoint (e.g., '/api/workers')
 * @param options - Request options
 * @returns Promise with response data
 */
export async function apiClient<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  // Production guard: Block localhost/HTTP requests
  if (isProduction()) {
    if (url.includes('localhost') || url.includes('127.0.0.1') || url.startsWith('http://')) {
      throw new ApiError(
        'Production API requests cannot use localhost or HTTP',
        400,
        endpoint
      );
    }
  }

  // Get auth token
  const token = options.skipAuth ? null : await getAuthToken();

  // Build headers
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const existingRequestId =
    (headers as Record<string, string>)['x-vh-req-id'] ||
    (headers as Record<string, string>)['X-VH-Req-Id'];
  const requestId = existingRequestId || createRequestId();
  (headers as Record<string, string>)['x-vh-req-id'] = requestId;

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Destructure body, skipAuth, AND headers from options so the `restOptions`
  // spread below cannot clobber the fully-built `headers` object.
  //
  // REGRESSION GUARD (2026-04-22): when a caller passed
  // `api.post(url, body, { headers: { 'X-CSRF-Token': '...' } })`, the old
  // destructure left `headers` inside `restOptions`. The subsequent spread
  // `{ method, headers, ...restOptions }` then replaced the full built
  // `headers` object (Content-Type + Authorization + x-vh-req-id + caller
  // headers) with only `restOptions.headers` (just the caller-provided key),
  // dropping Content-Type. Browser then defaulted Content-Type to
  // `text/plain;charset=UTF-8` for the string body, Express bodyParser.json()
  // left req.body empty, and all express-validators on
  // POST /api/team-invites/accept returned 400 "Validation failed".
  //
  // Captured via mobile Safari DevTools network log on the first real-human
  // invite-accept test. `production-mimicking` local staging (cross-origin +
  // real TLS) would have caught this — see Phase 4 of the plan file.
  //
  // Note: `client.js` exists as a legacy sibling but is NOT used in
  // production because vite.config.js sets resolve.extensions
  // `['.tsx', '.ts', '.jsx', '.js', '.json']` — .ts wins. The test suite
  // uses an explicit `./client.ts` import to target this file unambiguously.
  const { body, skipAuth, headers: _optionsHeaders, ...restOptions } = options;

  // Build request. `restOptions` no longer contains `headers`, so spreading
  // it cannot override our built `headers`.
  const requestInit: RequestInit = {
    method,
    headers,
    ...restOptions,
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    requestInit.body = JSON.stringify(body);
  }

  // Make request
  const startTime = Date.now();

  try {
    const response = await fetch(url, requestInit);
    const duration = Date.now() - startTime;

    // Log request (development only)
    if (!isProduction()) {
      console.debug(`[API] ${method} ${endpoint} - ${response.status} (${duration}ms)`);
    }

    // Handle non-OK responses
    if (!response.ok) {
      if (response.status === 401 && !options.skipAuth && !options._authRetry) {
        try {
          const refreshed = await getAuthTokenAsync({ forceRefresh: true });
          if (refreshed) {
            return apiClient<T>(method, endpoint, { ...options, _authRetry: true });
          }
        } catch {
          // Refresh failed — surface the original 401 below.
        }
      }

      let errorData: unknown;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: response.statusText };
      }

      const errorMessage = normalizeApiErrorMessage(errorData, response.status);

      throw new ApiError(errorMessage, response.status, endpoint, errorData);
    }

    // Parse response
    const data = await response.json();
    return data as T;
  } catch (error) {
    // Re-throw ApiErrors
    if (error instanceof ApiError) {
      throw error;
    }

    // Wrap network errors
    throw new ApiError(
      error instanceof Error ? error.message : 'Network error',
      0,
      endpoint
    );
  }
}

// ============================================================================
// Convenience Methods
// ============================================================================

export const api = {
  /**
   * GET request
   */
  get: <T>(endpoint: string, options?: RequestOptions): Promise<T> =>
    apiClient<T>('GET', endpoint, options),

  /**
   * POST request
   */
  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    apiClient<T>('POST', endpoint, { ...options, body }),

  /**
   * PUT request
   */
  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    apiClient<T>('PUT', endpoint, { ...options, body }),

  /**
   * PATCH request
   */
  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    apiClient<T>('PATCH', endpoint, { ...options, body }),

  /**
   * DELETE request
   */
  delete: <T>(endpoint: string, options?: RequestOptions): Promise<T> =>
    apiClient<T>('DELETE', endpoint, options),

  /**
   * GET request returning Blob (for file downloads)
   */
  getBlob: async (endpoint: string, options?: RequestOptions): Promise<Blob> => {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const token = options?.skipAuth ? null : await getAuthToken();
    const headers: HeadersInit = {
      ...(options?.headers as Record<string, string>),
    };
    const requestId = createRequestId();
    (headers as Record<string, string>)['x-vh-req-id'] = requestId;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Strip headers/body/skipAuth out of options before spreading so the
    // spread cannot clobber the fully-built `headers` (same regression
    // class as apiClient — see 2026-04-22 guard comment above).
    const { headers: _optionsHeaders, skipAuth: _skipAuth, body: _body, ...restOptions } = options ?? {};
    const response = await fetch(url, {
      method: 'GET',
      headers,
      ...restOptions,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new ApiError(
        (errorData as { message?: string })?.message || `Download failed: ${response.status}`,
        response.status,
        endpoint,
        errorData
      );
    }
    return await response.blob();
  },

  /**
   * POST request with FormData (for file uploads)
   */
  postForm: async <T>(endpoint: string, formData: FormData, options?: RequestOptions): Promise<T> => {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const token = options?.skipAuth ? null : await getAuthToken();
    const headers: HeadersInit = {
      // Don't set Content-Type - browser will set it with boundary
      ...(options?.headers as Record<string, string>),
    };
    const requestId = createRequestId();
    (headers as Record<string, string>)['x-vh-req-id'] = requestId;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Strip headers/body/skipAuth out of options before spreading so the
    // spread cannot clobber the fully-built `headers` or the FormData body
    // (same regression class as apiClient — see 2026-04-22 guard comment).
    const { headers: _optionsHeaders, skipAuth: _skipAuth, body: _body, ...restOptions } = options ?? {};
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      ...restOptions,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new ApiError(
        (errorData as { message?: string })?.message || `Upload failed: ${response.status}`,
        response.status,
        endpoint,
        errorData
      );
    }
    return await response.json() as T;
  },
};

export default api;
